import { styled } from "styled-components";
import { ConsentState } from "./domain/ConsentState";
import Informing, { InformingConsentText } from "./requesting/Informing";
import Confirming from "./confirming/Confirming";
import { RedirectText } from "../RedirectComponent";
import { HashSigningInstructionsProps } from "./requesting/HashSigningInstructions";
import { UseCase } from "../base/UseCase";
import ProcessContextBlock, {
  ClientConsentContext,
} from "../base/ProcessContextBlock";
import { ProcessStateImageProps } from "../base/ProcessStateImage";
import { ClientNameBoxProps } from "../base/ClientNameBox";

export interface ConsentComponentProps {
  relyingPartyName?: string | undefined;
  clientContextText?: string | undefined;
  title: string;
  logoUri?: string | undefined;
  attributes?: Array<string> | undefined;
  state: ConsentState;
  useCase: UseCase;
  isDesktop: boolean;
  consentTextFields: ConsentTextFields;
  hashSigningInformingInstructions?: HashSigningInstructionsProps | undefined;
  showNonPKIoWarning: boolean;
  redirectAfter?: number | undefined;
}

export interface ConsentTextFields {
  informing: InformingConsentText;
  confirming: {
    redirecting: RedirectText;
  };
}

// share with authentication layout
const ConsentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-height: 1px;
  height: auto;
  gap: 24px;
  background: transparent;
  border-radius: 0;
  box-sizing: border-box;
`;

// share with authentication layout
const StateBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function consentStateHandler({
  state,
  useCase,
  relyingPartyName,
  attributes,
  isDesktop,
  consentTextFields,
  hashSigningInformingInstructions: hashSigningInstructions,
  showNonPKIoWarning,
}: ConsentComponentProps): JSX.Element {
  switch (state.type) {
    case "ConsentRequested":
      return (
        <Informing
          isDesktop={isDesktop}
          t={consentTextFields.informing}
          attributes={attributes}
          hashSigningInstructions={
            useCase === "HashSigning" ? hashSigningInstructions : undefined
          }
          showNonPKIoAlert={showNonPKIoWarning}
          useCase={useCase}
        />
      );
    case "ConsentGranted":
      return (
        <Confirming
          redirectToUri={state.redirectUri}
          t={{
            redirect: consentTextFields.confirming.redirecting,
          }}
          clientName={relyingPartyName}
        />
      );
  }
}

function setProcessStateImage(
  useCase: UseCase,
  state: ConsentState,
): ProcessStateImageProps | undefined {
  switch (useCase) {
    case "HashSigning": {
      switch (state.type) {
        case "ConsentRequested": {
          return { state: { type: "Consenting" } };
        }
        case "ConsentGranted": {
          return { state: { type: "Consented" } };
        }
      }
    }
    case "EasyLogin":
    case "Identification": {
      return undefined;
    }
  }
}

function setClientConsentContext(
  useCase: UseCase,
  state: ConsentState,
  headerText: string,
  clientContextText: string | undefined,
): ClientConsentContext | undefined {
  switch (useCase) {
    case "HashSigning":
      return undefined;
    case "EasyLogin":
    case "Identification":
      switch (state.type) {
        case "ConsentRequested":
          return {
            clientContextHeaderText: headerText,
            clientContextText: clientContextText,
          };
        case "ConsentGranted":
          return undefined;
      }
  }
}

function setClientNameBox(
  clientName: string | undefined,
  clientLogo: string | undefined,
  display: boolean,
): ClientNameBoxProps | undefined {
  if (clientName !== undefined) {
    return { clientName, clientLogo, display };
  } else {
    return undefined;
  }
}

export default function ConsentComponent(props: ConsentComponentProps) {
  return (
    <ConsentBox>
      <ProcessContextBlock
        title={props.title}
        isDesktop={props.isDesktop}
        clientNameBox={setClientNameBox(
          props.relyingPartyName,
          props.logoUri,
          true,
        )}
        clientConsentContext={setClientConsentContext(
          props.useCase,
          props.state,
          props.consentTextFields.informing.clientContextHeaderText,
          props.clientContextText,
        )}
        processStateImage={setProcessStateImage(props.useCase, props.state)}
        extraContent={undefined}
        displayClientConsentContext={props.clientContextText !== undefined}
      />
      <StateBox>{consentStateHandler(props)}</StateBox>
    </ConsentBox>
  );
}
